// Vuex module for authorization: manages user roles, permissions, and org ID
import { postApiData, fetchApiData } from '@/helpers/api'

const bitsAdminPermissions = ['manage_all_users', 'manage_system', 'read_all_resources', 'write_all_resources', 'view_reports', 'create_users', 'access_camera', 'access_requests']

export default {
  namespaced: true,
  state: {
    userRole: null,
    permissions: [],
    organizationId: null,
    canAccess: {}, // Cache for permission checks
    initialized: false
  },
  getters: {
    hasPermission: (state) => (permission) => {
      return state.permissions.includes(permission)
    },
    hasRole: (state) => (role) => {
      return state.userRole === role
    },
    userRole: (state) => state.userRole,
    isAdmin: (state) => {
      return ['bits_admin', 'superadmin', 'bits_user'].includes(state.userRole)
    },
    isOrgAdmin: (state) => {
      return state.userRole === 'customer_owner'
    },
    isOrgMember: (state) => {
      return ['customer_owner', 'customer_user'].includes(state.userRole)
    },
    canAccessResource: (state) => (resourceOwnerId, resourceOrgId = null) => {
      const cacheKey = `${resourceOwnerId}-${resourceOrgId || 'null'}`
      return state.canAccess[cacheKey] || false
    }
  },
  mutations: {
    setUserRole (state, role) {
      state.userRole = role
    },
    setPermissions (state, permissions) {
      state.permissions = permissions
    },
    setOrganizationId (state, orgId) {
      state.organizationId = orgId
    },
    setResourceAccess (state, { key, canAccess }) {
      state.canAccess[key] = canAccess
    },
    setInitialized (state, initialized) {
      state.initialized = initialized
    },
    clearAuthorization (state) {
      state.userRole = null
      state.permissions = []
      state.organizationId = null
      state.canAccess = {}
      state.initialized = false
    }
  },
  actions: {
    async initializeAuthorization ({ commit, dispatch, rootGetters }) {
      try {
        const userId = rootGetters['auth/getUserId']
        const token = window.sessionStorage.getItem('ACCESS_TOKEN')

        // If no user ID but we have a token, still try to initialize
        if (!userId && !token) {
          return false
        }

        // Get user role and permissions
        const userRole = await dispatch('fetchUserRole', userId)
        if (userRole) {
          commit('setUserRole', userRole.role)
          commit('setOrganizationId', userRole.organization_id)

          // Get permissions for this role
          const permissions = await dispatch('fetchRolePermissions', userRole.role)
          commit('setPermissions', permissions)

          commit('setInitialized', true)
          return true
        } else if (token) {
          // We have a token but couldn't get user role - set default permissions
          commit('setUserRole', 'bits_user')
          commit('setOrganizationId', null)
          commit('setPermissions', ['read_all_resources', 'view_reports', 'create_users', 'access_camera', 'access_requests'])
          commit('setInitialized', true)
          return true
        }
        return false
      } catch (error) {
        // If initialization fails but we have a token, set basic permissions
        const token = window.sessionStorage.getItem('ACCESS_TOKEN')
        if (token) {
          commit('setUserRole', 'bits_user')
          commit('setOrganizationId', null)
          commit('setPermissions', ['read_all_resources', 'view_reports', 'create_users', 'access_camera', 'access_requests'])
          commit('setInitialized', true)
          return true
        }
        return false
      }
    },

    async fetchUserRole (context, userId) {
      try {
        // Use the correct endpoint from bits-backend
        const response = await fetchApiData('api/auth/roles/me')

        if (response && !response.active) {
          await this.dispatch('auth/signOut')
          // Return null immediately to stop authorization flow for inactive users
          // This prevents the router and UI from continuing as authenticated
          return null
        }

        if (response && response.role) {
          return {
            role: response.role,
            organization_id: response.organization_id || null
          }
        }
        // If no role returned, check if user exists and assign default role
        if (response && response.id) {
          return {
            role: 'bits_user',
            organization_id: response.organization_id || null
          }
        }
        // Fallback - assume basic user role if no role is returned
        return {
          role: 'bits_user',
          organization_id: null
        }
      } catch (error) {
        console.warn('Failed to fetch user role from /api/auth/roles/me:', error.message)

        // Final fallback for authenticated users - assume basic user role
        const token = window.sessionStorage.getItem('ACCESS_TOKEN')
        if (token) {
          console.log('Using fallback role for authenticated user')
          return {
            role: 'bits_user',
            organization_id: null
          }
        }

        return null
      }
    },

    async fetchRolePermissions (context, role) {
      try {
        // For now, return basic permissions based on role until proper RBAC is implemented
        const rolePermissions = {
          bits_admin: bitsAdminPermissions,
          superadmin: bitsAdminPermissions,
          customer_owner: ['manage_org_users', 'read_org_resources', 'write_org_resources', 'view_reports', 'create_users', 'manage_all_users', 'access_camera', 'access_requests'],
          customer_user: ['read_org_resources', 'view_reports', 'create_users', 'access_camera', 'access_requests'], // Ensure all users can access camera and requests
          resource_manager: ['manage_org_users', 'read_org_resources', 'write_org_resources', 'view_reports', 'create_users', 'access_camera', 'access_requests'],
          basic_user: [] // No permissions - restricted access
        }
        return rolePermissions[role] || ['read_all_resources', 'view_reports', 'create_users', 'access_camera', 'access_requests'] // Default permissions for unknown roles with camera and request access
      } catch (error) {
        // Fallback permissions for any role - ensure camera and request access
        return ['read_all_resources', 'view_reports', 'create_users', 'access_camera', 'access_requests']
      }
    },

    async checkPermission ({ state, commit }, permission) {
      try {
        if (!state.initialized) {
          await this.dispatch('authorization/initializeAuthorization')
        }

        // Check local state first
        if (state.permissions.includes(permission)) {
          return true
        }

        // For development and when backend is not fully configured,
        // allow key permissions for authenticated users
        const developmentPermissions = ['read_all_resources', 'view_reports', 'create_users', 'access_camera', 'access_requests']
        if (developmentPermissions.includes(permission)) {
          const token = window.sessionStorage.getItem('ACCESS_TOKEN')
          if (token) {
            return true
          }
        }

        // Fallback to server check if available
        try {
          const response = await fetchApiData(`api/auth/permissions/check?permission=${permission}`)
          return response.hasPermission || false
        } catch (serverError) {
          // Server endpoint doesn't exist, use local logic
          // Allow basic permissions for authenticated users
          const token = window.sessionStorage.getItem('ACCESS_TOKEN')
          if (token && developmentPermissions.includes(permission)) {
            return true
          }
          return false
        }
      } catch (error) {
        // If all else fails, allow basic read permissions for authenticated users
        const token = window.sessionStorage.getItem('ACCESS_TOKEN')
        const basicPermissions = ['read_all_resources', 'view_reports', 'create_users', 'access_camera', 'access_requests']
        return token && basicPermissions.includes(permission)
      }
    },

    async checkResourceAccess ({ state, commit }, { resourceOwnerId, organizationId = null }) {
      try {
        const cacheKey = `${resourceOwnerId}-${organizationId || 'null'}`

        // Check cache first
        if (state.canAccess[cacheKey] !== undefined) {
          return state.canAccess[cacheKey]
        }

        // bits-backend doesn't have this specific endpoint, return true for now
        // This would need to be implemented in bits-backend for full resource access control
        const canAccess = true
        commit('setResourceAccess', { key: cacheKey, canAccess })
        return canAccess
      } catch (error) {
        return false
      }
    },

    async setUserRole ({ commit, dispatch }, { userId, role, organizationId = null }) {
      try {
        const response = await postApiData('api/auth/roles', {
          userId,
          role,
          organizationId
        })

        if (response.success) {
          // Refresh authorization if setting role for current user
          const currentUserId = this.getters['auth/getUserId']
          if (userId === currentUserId) {
            await dispatch('initializeAuthorization')
          }
        }

        return response
      } catch (error) {
        return { success: false, error: error.message }
      }
    }
  }
}
